import type { Skill } from '../domain.js';

/**
 * Математика, 3–6 клас НУШ.
 *
 * `generators` — id процедурних генераторів у @lk/engine. Кожен генератор
 * зобов'язаний робити дистрактори на основі ТИПОВИХ ПОМИЛОК (забув перенос,
 * переплутав дію, помилка на одиницю), а не випадкових чисел.
 */
export const MATH_SKILLS: Skill[] = [
  // ── Арифметика ──────────────────────────────────────────────────────────
  {
    id: 'math.arith.add_sub_100',
    subject: 'math',
    strand: 'arith',
    title: 'Додавання і віднімання до 100',
    description: 'Рахує в межах 100 з переходом через десяток, усно.',
    gradeBand: [2, 4],
    prerequisites: [],
    baseDifficulty: 820,
    foundational: true,
    generators: ['math.addSub'],
  },
  {
    id: 'math.arith.add_sub_1000',
    subject: 'math',
    strand: 'arith',
    title: 'Додавання і віднімання до 1000',
    description: 'Додає й віднімає трицифрові числа, зокрема з переходом.',
    gradeBand: [3, 5],
    prerequisites: ['math.arith.add_sub_100'],
    baseDifficulty: 950,
    generators: ['math.addSub'],
  },
  {
    id: 'math.arith.mul_table',
    subject: 'math',
    strand: 'arith',
    title: 'Таблиця множення',
    description: 'Знає таблицю множення до 10×10 напам\'ять.',
    gradeBand: [2, 4],
    prerequisites: [],
    baseDifficulty: 880,
    foundational: true,
    generators: ['math.mulTable'],
  },
  {
    id: 'math.arith.div_table',
    subject: 'math',
    strand: 'arith',
    title: 'Табличне ділення',
    description: 'Ділить у межах таблиці множення, розуміє зв\'язок з множенням.',
    gradeBand: [3, 4],
    prerequisites: ['math.arith.mul_table'],
    baseDifficulty: 930,
    foundational: true,
    generators: ['math.divTable'],
  },
  {
    id: 'math.arith.mul_multi_digit',
    subject: 'math',
    strand: 'arith',
    title: 'Множення багатоцифрових',
    description: 'Множить дво- і трицифрові числа письмово.',
    gradeBand: [4, 6],
    prerequisites: ['math.arith.mul_table'],
    baseDifficulty: 1080,
    generators: ['math.mulMultiDigit'],
  },
  {
    id: 'math.arith.div_remainder',
    subject: 'math',
    strand: 'arith',
    title: 'Ділення з остачею',
    description: 'Ділить з остачею й пояснює, що означає остача.',
    gradeBand: [4, 6],
    prerequisites: ['math.arith.div_table'],
    baseDifficulty: 1050,
    generators: ['math.divRemainder'],
  },
  {
    id: 'math.arith.order_of_ops',
    subject: 'math',
    strand: 'arith',
    title: 'Порядок дій і дужки',
    description: 'Обчислює вирази з кількома діями та дужками в правильному порядку.',
    gradeBand: [3, 6],
    prerequisites: ['math.arith.mul_table', 'math.arith.add_sub_100'],
    baseDifficulty: 1060,
    foundational: true,
    generators: ['math.orderOfOps'],
  },
  {
    id: 'math.arith.negative',
    subject: 'math',
    strand: 'arith',
    title: 'Від\'ємні числа',
    description: 'Порівнює й додає від\'ємні числа, працює з числовою прямою.',
    gradeBand: [6, 6],
    prerequisites: ['math.arith.add_sub_1000'],
    baseDifficulty: 1240,
    generators: ['math.negative'],
  },

  // ── Числа і розряди ─────────────────────────────────────────────────────
  {
    id: 'math.number.place_value',
    subject: 'math',
    strand: 'number',
    title: 'Розряди і округлення',
    description: 'Читає великі числа, визначає розряди, округлює.',
    gradeBand: [3, 5],
    prerequisites: [],
    baseDifficulty: 900,
    generators: ['math.placeValue'],
  },
  {
    id: 'math.number.divisibility',
    subject: 'math',
    strand: 'number',
    title: 'Ознаки подільності',
    description: 'Застосовує ознаки подільності на 2, 3, 5, 9, 10.',
    gradeBand: [5, 6],
    prerequisites: ['math.arith.div_table'],
    baseDifficulty: 1150,
    generators: ['math.divisibility'],
  },
  {
    id: 'math.number.primes_factors',
    subject: 'math',
    strand: 'number',
    title: 'Прості числа, НСД і НСК',
    description: 'Розкладає на множники, знаходить НСД і НСК.',
    gradeBand: [5, 6],
    prerequisites: ['math.number.divisibility'],
    baseDifficulty: 1230,
    generators: ['math.primesFactors'],
  },

  // ── Дроби ───────────────────────────────────────────────────────────────
  {
    id: 'math.fractions.concept',
    subject: 'math',
    strand: 'fractions',
    title: 'Частини цілого',
    description: 'Розуміє дріб як частину цілого, знаходить частину від числа.',
    gradeBand: [3, 5],
    prerequisites: ['math.arith.div_table'],
    baseDifficulty: 990,
    foundational: true,
    generators: ['math.fractionConcept'],
  },
  {
    id: 'math.fractions.compare',
    subject: 'math',
    strand: 'fractions',
    title: 'Порівняння дробів',
    description: 'Порівнює дроби з різними знаменниками, зводить до спільного.',
    gradeBand: [5, 6],
    prerequisites: ['math.fractions.concept'],
    baseDifficulty: 1140,
    generators: ['math.fractionCompare'],
  },
  {
    id: 'math.fractions.add_sub',
    subject: 'math',
    strand: 'fractions',
    title: 'Додавання і віднімання дробів',
    description: 'Додає й віднімає дроби, зокрема з різними знаменниками.',
    gradeBand: [5, 6],
    prerequisites: ['math.fractions.compare'],
    baseDifficulty: 1220,
    generators: ['math.fractionAddSub'],
  },
  {
    id: 'math.fractions.mul_div',
    subject: 'math',
    strand: 'fractions',
    title: 'Множення і ділення дробів',
    description: 'Множить і ділить звичайні дроби, скорочує результат.',
    gradeBand: [6, 6],
    prerequisites: ['math.fractions.add_sub'],
    baseDifficulty: 1300,
    generators: ['math.fractionMulDiv'],
  },

  // ── Десяткові дроби і відсотки ──────────────────────────────────────────
  {
    id: 'math.decimals.arith',
    subject: 'math',
    strand: 'decimals',
    title: 'Десяткові дроби',
    description: 'Читає, порівнює, додає й віднімає десяткові дроби.',
    gradeBand: [5, 6],
    prerequisites: ['math.number.place_value'],
    baseDifficulty: 1170,
    generators: ['math.decimalArith'],
  },
  {
    id: 'math.percent.basic',
    subject: 'math',
    strand: 'percent',
    title: 'Відсотки',
    description: 'Знаходить відсоток від числа й число за його відсотком.',
    gradeBand: [5, 6],
    prerequisites: ['math.fractions.concept'],
    baseDifficulty: 1210,
    generators: ['math.percent'],
  },
  {
    id: 'math.ratio.proportion',
    subject: 'math',
    strand: 'ratio',
    title: 'Відношення і пропорції',
    description: 'Складає пропорцію і розв\'язує задачі на пряму пропорційність.',
    gradeBand: [6, 6],
    prerequisites: ['math.percent.basic'],
    baseDifficulty: 1290,
    generators: ['math.proportion'],
  },

  // ── Рівняння ────────────────────────────────────────────────────────────
  {
    id: 'math.equations.simple',
    subject: 'math',
    strand: 'equations',
    title: 'Рівняння з невідомим',
    description: 'Знаходить невідомий доданок, зменшуване, множник.',
    gradeBand: [3, 5],
    prerequisites: ['math.arith.add_sub_100'],
    baseDifficulty: 1010,
    generators: ['math.simpleEquation'],
  },
  {
    id: 'math.equations.linear',
    subject: 'math',
    strand: 'equations',
    title: 'Лінійні рівняння',
    description: 'Розв\'язує рівняння виду ax + b = c з перенесенням доданків.',
    gradeBand: [6, 6],
    prerequisites: ['math.equations.simple', 'math.arith.order_of_ops'],
    baseDifficulty: 1280,
    generators: ['math.linearEquation'],
  },

  // ── Величини ────────────────────────────────────────────────────────────
  {
    id: 'math.measure.units',
    subject: 'math',
    strand: 'measure',
    title: 'Одиниці вимірювання',
    description: 'Переводить одиниці довжини, маси, місткості.',
    gradeBand: [3, 6],
    prerequisites: ['math.number.place_value'],
    baseDifficulty: 1000,
    generators: ['math.unitConversion'],
  },
  {
    id: 'math.measure.time',
    subject: 'math',
    strand: 'measure',
    title: 'Час і тривалість',
    description: 'Визначає час за годинником, рахує тривалість подій.',
    gradeBand: [3, 5],
    prerequisites: [],
    baseDifficulty: 960,
    generators: ['math.timeCalc'],
  },
  {
    id: 'math.measure.money',
    subject: 'math',
    strand: 'measure',
    title: 'Гроші й решта',
    description: 'Рахує вартість покупки, решту, порівнює вигідність.',
    gradeBand: [3, 6],
    prerequisites: ['math.arith.add_sub_100'],
    baseDifficulty: 1020,
    generators: ['math.money'],
  },

  // ── Геометрія ───────────────────────────────────────────────────────────
  {
    id: 'math.geometry.shapes',
    subject: 'math',
    strand: 'geometry',
    title: 'Геометричні фігури',
    description: 'Розпізнає фігури та їхні властивості, рахує елементи.',
    gradeBand: [3, 6],
    prerequisites: [],
    baseDifficulty: 930,
    generators: ['math.shapes'],
  },
  {
    id: 'math.geometry.perimeter_area',
    subject: 'math',
    strand: 'geometry',
    title: 'Периметр і площа',
    description: 'Обчислює периметр і площу прямокутника, квадрата, трикутника.',
    gradeBand: [4, 6],
    prerequisites: ['math.arith.mul_table', 'math.geometry.shapes'],
    baseDifficulty: 1090,
    generators: ['math.perimeterArea'],
  },
  {
    id: 'math.geometry.volume',
    subject: 'math',
    strand: 'geometry',
    title: 'Об\'єм',
    description: 'Обчислює об\'єм прямокутного паралелепіпеда.',
    gradeBand: [5, 6],
    prerequisites: ['math.geometry.perimeter_area'],
    baseDifficulty: 1200,
    generators: ['math.volume'],
  },
  {
    id: 'math.geometry.angles',
    subject: 'math',
    strand: 'geometry',
    title: 'Кути',
    description: 'Класифікує кути, знаходить невідомий кут.',
    gradeBand: [5, 6],
    prerequisites: ['math.geometry.shapes'],
    baseDifficulty: 1160,
    generators: ['math.angles'],
  },

  // ── Текстові задачі ─────────────────────────────────────────────────────
  {
    id: 'math.word.one_step',
    subject: 'math',
    strand: 'word',
    title: 'Прості задачі',
    description: 'Розв\'язує задачу в одну дію, правильно обирає дію.',
    gradeBand: [2, 4],
    prerequisites: ['math.arith.add_sub_100'],
    baseDifficulty: 950,
    foundational: true,
    generators: ['math.wordOneStep'],
  },
  {
    id: 'math.word.two_step',
    subject: 'math',
    strand: 'word',
    title: 'Складені задачі',
    description: 'Розв\'язує задачу у дві-три дії, будує план розв\'язання.',
    gradeBand: [4, 6],
    prerequisites: ['math.word.one_step', 'math.arith.order_of_ops'],
    baseDifficulty: 1130,
    foundational: true,
    generators: ['math.wordTwoStep'],
  },
  {
    id: 'math.word.speed',
    subject: 'math',
    strand: 'word',
    title: 'Задачі на рух',
    description: 'Зв\'язує швидкість, час і відстань.',
    gradeBand: [4, 6],
    prerequisites: ['math.word.two_step'],
    baseDifficulty: 1220,
    generators: ['math.wordSpeed'],
  },
  {
    id: 'math.word.work',
    subject: 'math',
    strand: 'word',
    title: 'Задачі на продуктивність',
    description: 'Рахує спільну роботу, продуктивність і час виконання.',
    gradeBand: [5, 6],
    prerequisites: ['math.word.two_step'],
    baseDifficulty: 1270,
    generators: ['math.wordWork'],
  },

  // ── Дані ────────────────────────────────────────────────────────────────
  {
    id: 'math.data.average',
    subject: 'math',
    strand: 'data',
    title: 'Середнє арифметичне',
    description: 'Знаходить середнє арифметичне і застосовує його в задачах.',
    gradeBand: [5, 6],
    prerequisites: ['math.arith.div_table'],
    baseDifficulty: 1140,
    generators: ['math.average'],
  },
  {
    id: 'math.data.tables_charts',
    subject: 'math',
    strand: 'data',
    title: 'Таблиці та діаграми',
    description: 'Читає дані з таблиці й стовпчикової діаграми, порівнює.',
    gradeBand: [4, 6],
    prerequisites: [],
    baseDifficulty: 1050,
    generators: ['math.dataReading'],
  },
];
